/** The kind of action a destination route implies the user is trying to perform. */
export enum E_USER_INTENT {
  Invest = 'INVEST',
  Trade = 'TRADE',
}

// Trading surface: digital assets / crypto / wallet, INX, secondary-market
// trading of tokens. Republic Wallet partner flows (`/rw`) live here too.
const TRADE_PREFIXES = [
  '/digital-assets',
  '/crypto',
  '/trade',
  '/trade-with-inx',
  '/rw',
  '/inx-deposit',
  '/inx-allowance',
  // Monolith crypto portfolio — checked before the `/portfolio` invest prefix.
  '/portfolio/crypto',
];

// Investment surface: primary offerings and the investor's portfolio/account.
const INVEST_PREFIXES = [
  '/invest',
  '/portfolio',
  '/r/portfolio',
  '/investments',
  '/settings/investment-entity',
];

// Not a consumer onboarding destination — never classified.
const ADMIN_PREFIXES = ['/admin'];

// Offering deal-page actions: `/<slug>/invest`, `/<slug>/reserve`, `/<slug>/setup`
// all route to an investment (monolith `investments#new`).
const OFFERING_INVEST_REGEX = /^\/[a-zA-Z0-9_-]+\/(invest|reserve|setup)\/?$/;

// Airdrop token claim: `/<slug>/claim` → `airdrop/investments#new` (Trade).
// `/note/claim` (republic_note_claim) shares this shape but is intentionally not
// classified, and airdrop slugs can't be told apart client-side, so exclude `note`.
const CLAIM_REGEX = /^\/([a-zA-Z0-9_-]+)\/claim\/?$/;

const matchesPrefix = (path: string, prefixes: string[]): boolean =>
  prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Classifies a destination route into the action the user is trying to perform
 * (investing vs trading), or `null` when the route implies no specific action
 * (admin, generic, marketing, or unrecognized paths).
 *
 * Route-classification only — intentionally unaware of onboarding, GraphQL, or any
 * consumer. Accepts absolute URLs or relative paths; query string and fragment are
 * ignored.
 */
export const getUserIntentFromRoute = (
  destination?: string
): E_USER_INTENT | null => {
  if (!destination) return null;

  let path: string;
  try {
    path = new URL(destination, 'https://fallback-for-relative-paths').pathname;
  } catch {
    return null;
  }

  if (matchesPrefix(path, ADMIN_PREFIXES)) return null;

  const claimSlug = path.match(CLAIM_REGEX)?.[1];
  if (
    matchesPrefix(path, TRADE_PREFIXES) ||
    (claimSlug != null && claimSlug !== 'note')
  ) {
    return E_USER_INTENT.Trade;
  }

  if (
    matchesPrefix(path, INVEST_PREFIXES) ||
    OFFERING_INVEST_REGEX.test(path)
  ) {
    return E_USER_INTENT.Invest;
  }

  return null;
};
